import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../../data/queries/use-project';
import { getSupabaseClient } from '../../data/supabase-client';
import { createCapture, describeCreateCaptureError } from '../../data/writers/create-capture';
import type { CreateCaptureInput } from '../../data/writers/create-capture';
import { transcribeRecording } from '../../data/stt-client';
// MOVED, NOT COPIED (M9 / S09-04). The typed-text + transcript merge rule this
// screen established is now shared with Ask Sol's microphone. Behaviour is
// unchanged; a second copy would be how the two screens start disagreeing.
import { deliverTranscript } from '../dictation/dictation-text';
import { selectRecorder } from '../../platform/recorder';
import { selectKeepAwake } from '../../platform/keep-awake';
import { readCaptureTimeZone } from '../../lib/timezone';
import { reportError } from '../../lib/monitoring';
import { sessionOwnerUserId, useOptionalSession } from '../auth/session';
import {
  CAPTURE_DRAFT_SCHEMA_VERSION,
  getCaptureDraftStore,
  isRecoverableCaptureDraft,
  type CaptureDraft,
  type CaptureDraftPendingPayload,
} from '../../data/drafts/capture-draft';
import {
  buildCaptureAudioRecord,
  getCaptureAudioStore,
  isOrphanCaptureAudio,
  matchCaptureAudio,
  type CaptureAudioMatch,
  type CaptureAudioRecord,
} from '../../data/drafts/capture-audio';
import { CaptureRecoveryCard } from './CaptureRecoveryCard';
import { RECORDING_HARD_CAP_MS, RECORDING_WARNING_MS } from './recording-limits';
import { PROJECT_STATUS_LABEL } from '../projects/status';
import {
  CAPTURE_STATUS_LABEL,
  CAPTURE_STATUS_ORDER,
  formatCaptureNumber,
  isBlankTranscript,
} from './capture-status';
import { requestCaptureOrganizationInBackground } from '../../data/organize-client';
import type { CaptureRow, CaptureStatus } from '../../data/database.types';
import './captures.css';

/**
 * Fast Capture — M3 / S-03-02 (typed) + S-03-03 (spoken). Design export S08/S09
 * (record → transcribe → review & save) and S10 (saved confirmation).
 *
 * ONE SCREEN, TWO WAYS IN, ONE TRUTH OUT. Speaking and typing both land in the
 * same `note` value, and Save sends `note` — there is no second transcript
 * variable anywhere in this file, so what the user sees IS what is saved (the
 * Final Accepted Transcript, PT §8). STT output is a DRAFT until Brandon
 * accepts it by saving; the review step is where "his words" become "his
 * record".
 *
 * **TYPED ENTRY IS PERMANENT PRODUCT SURFACE, NOT SCAFFOLDING.** PT §7 requires
 * "type the note" as the fallback when recording or transcription fails, and
 * the note field on this screen is that fallback — always present, never more
 * than a glance away from any failure state.
 *
 * THE TRANSCRIPT REVIEW IS VIEW-FIRST (PT §7): when a transcript arrives it is
 * shown as TEXT, the keyboard does not open, and nothing is focused. A small
 * Edit control switches to the textarea only when Brandon deliberately asks.
 * Normal posture is Stop → Transcribe → glance → Save.
 *
 * THE RECORDING LIFECYCLE IS HARDENED — S-03-04, values and shape from D-008:
 *
 *   * ONE SOFT WARNING at ~2 minutes (`RECORDING_WARNING_MS`). Recording
 *     CONTINUES — the warning is a nudge toward a focused Capture, fires once,
 *     never blocks, never spams, and resets for a genuinely new recording.
 *   * ONE HARD SAFETY CAP at ~10 minutes (`RECORDING_HARD_CAP_MS`). The cap
 *     bounds a forgotten or runaway recorder: recording stops through the SAME
 *     safe stop path as the Stop button, everything recorded is preserved, and
 *     Brandon is told why it stopped and what he can do next. No auto-split,
 *     no automatic Part 2 — a new Capture starts only by his action.
 *   * A SAFE STOP NEVER TRANSCRIBES BY ITSELF. Every safely completed
 *     recording — user Stop or cap Stop — waits, in memory, for an EXPLICIT
 *     choice: **Transcribe** sends it through the existing authenticated STT
 *     path exactly once; **Discard** throws away THIS unsaved recording only,
 *     sends nothing to STT, and returns the screen to a usable idle where
 *     typing and recording again both stand.
 *   * KEEP-AWAKE DURING ACTIVE RECORDING ONLY (`KeepAwakePort`), acquired when
 *     recording genuinely starts and released on EVERY exit — Stop, cap,
 *     stop failure, interruption, Cancel, unmount. An unsupported or refusing
 *     runtime degrades silently: recording works exactly the same without it.
 *
 * IN-PROCESS INTERRUPTIONS FOLLOW D-018 — S-03-05, one umbrella rule:
 *
 *   * TRUSTWORTHY FINALIZATION → PRESERVE AND ASK. Crewlink leaving the
 *     screen (backgrounding and screen lock are one `visibilitychange` signal
 *     at this layer) and a microphone that died (the track's `ended` event,
 *     reported by the port) both route into the SAME safe stop path as the
 *     Stop button: the recording is preserved, the reason is stated, and the
 *     explicit Transcribe / Discard choice waits. Never recording off-screen.
 *   * UNCERTAIN FINALIZATION → NO SALVAGE. A recorder that ERRORED, or a stop
 *     that cannot finalize, is discarded per D-017 with an honest sentence,
 *     Record again and Type Note standing. Never auto-transcribe, never
 *     auto-save, never pretend an interrupted recording succeeded.
 *   * D-018 covers interruptions WHILE THE PROCESS IS ALIVE. Process death and
 *     cold relaunch are M4, and as of S-04-01 the textual half of that is here
 *     — see THE DURABLE DRAFT below. The AUDIO half (D-010) is still S-04-02
 *     and nothing in this file anticipates it.
 *
 * THE DURABLE DRAFT — M4 / S-04-01, under D-002 and D-019:
 *
 *   Everything on this screen that would otherwise die with the process is
 *   mirrored into a device-local draft: the note, the Capture status, the
 *   stable `client_capture_uuid`, and — before the request leaves the device —
 *   the frozen payload of a dispatched Save. Writes are debounced for ordinary
 *   typing and IMMEDIATE for the facts that change meaning: a recording
 *   starting, a Save being dispatched, a new draft beginning.
 *
 *   NOTHING HERE ASSUMES A GRACEFUL SHUTDOWN. Android kills a backgrounded app
 *   without warning, so the draft must already be on disk when it happens
 *   rather than written on the way down. Unmount cleanup is not the mechanism
 *   and is not relied on.
 *
 *   RECOVERY IS OFFERED, NEVER TAKEN (D-019 J-3). This screen restores a
 *   stored draft only when navigation carried an explicit recover intent; an
 *   ordinary visit that finds a stored draft shows the same recovery card
 *   instead and, until Brandon answers it, writes nothing — so starting a new
 *   Capture can never quietly overwrite an unfinished one.
 *
 *   NO AUDIO IS PERSISTED, in any form (D-019 J-1). A single boolean records
 *   that a recording was unresolved, and its only use is the honest sentence
 *   telling Brandon the recording itself could not be kept.
 *
 * AUDIO IS TRANSIENT. The recording lives in memory exactly as long as the
 * flow needs it — held while Brandon decides Transcribe or Discard, held for a
 * retry after a failed attempt, dropped the moment a transcript is delivered,
 * a new recording starts, it is discarded, or the attempt is cancelled.
 * Nothing writes it to storage, a database, or a bucket (D-001; D-010's
 * recovery lifecycle is M4 and nothing here anticipates it).
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT HAVE:
 *
 *   No title field, no AI caption, no structured fields, no location, no trade
 *   or category, no photos, no audio playback or retention. PT §5 is
 *   explicit that a location is never invented — M3 shows the "Location not
 *   set" fallback instead of guessing one.
 *
 * SAVING MAKES ZERO REASONING-MODEL CALLS (D-014, INV-T27). STT is input
 * acquisition and is allowed in the Capture path by design (D-011); there is
 * no Sol, no `AiReasoningPort` and no organization step anywhere in this file,
 * and Capture must save while the reasoning provider is entirely unavailable.
 */

/**
 * TWO FAILURE KINDS, split by ONE fact: did a request leave the device?
 * (S-03-02 R1 finding F-1.)
 *
 * `failed` — nothing was dispatched. No client, or the device could not supply
 * a timezone. The server cannot hold anything, so the draft stays editable.
 *
 * `failed-ambiguous` — `createCapture` was called and then threw. The request
 * may have reached the server and succeeded with only the RESPONSE lost — in
 * which case the server already holds the ORIGINAL payload under this draft's
 * idempotency key, and a retry will get that Capture back, not a new one.
 * Letting the note or status change now would let "Saved. CAP-####" render
 * over text the server never stored. So the exact dispatched payload is frozen
 * here, editing locks, and Retry resends the frozen payload verbatim.
 *
 * A server refusal (CL001 and friends) also lands here. It is knowably not a
 * lost success, but classifying refusal shapes to unlock editing would widen
 * this fix for no product benefit — no refusal is fixable by editing the note.
 */
/**
 * `waiting-to-send` is S-04-01's addition and is the SAME situation as
 * `failed-ambiguous` seen after a process death rather than after a throw: a
 * Save left this device and its answer was never received. It is a separate
 * state because the SENTENCE is different — a relaunched app has no failure to
 * report, only an unfinished send — and because D-002 locks the wording for
 * it: **"Waiting to send"**, never "Saved", never "Failed".
 *
 * Both carry the FROZEN payload, and both retry it verbatim. That is the whole
 * reconciliation mechanism: same key, same text, same status, so the server's
 * owner-scoped idempotency decides the truth and exactly one Capture exists
 * whether or not the first request landed.
 */
type SaveState =
  | { kind: 'editing' }
  | { kind: 'saving'; payload: CreateCaptureInput }
  | { kind: 'saved'; capture: CaptureRow }
  | { kind: 'failed'; message: string }
  | { kind: 'failed-ambiguous'; message: string; payload: CreateCaptureInput }
  | { kind: 'waiting-to-send'; payload: CaptureDraftPendingPayload };

/**
 * WHERE THIS SCREEN STANDS RELATIVE TO THE DURABLE DRAFT SLOT (S-04-01).
 *
 * `checking` — the slot has not been read yet. Nothing is written in this
 *              state, so a launch can never overwrite a draft it has not
 *              looked at.
 * `ready`    — this screen owns the slot. Either there was nothing stored, or
 *              Brandon answered the recovery card. Persistence runs here.
 * `offer`    — a stored draft was found and was NOT asked for. The card is
 *              shown and persistence stays off until he chooses, which is what
 *              makes "a new Capture never silently destroys an unfinished one"
 *              structural rather than a matter of him noticing.
 */
/**
 * A resolved recovery answer about audio: the verdict, plus the record itself
 * when there is one to hand back. Carrying the record avoids reading the store
 * twice and guarantees the Blob offered is the one that was validated.
 */
type RecoveredAudio =
  { match: 'none' | 'lost' } | { match: 'ready' | 'expired'; record: CaptureAudioRecord };

type DraftEntry =
  | { kind: 'checking' }
  | { kind: 'ready' }
  // `readAtMs` is stamped when the read RESOLVES, not when the card renders:
  // the clock is not readable during render, and an age fixed at read time is
  // the honest one anyway — it does not drift while Brandon looks at it.
  | { kind: 'offer'; draft: CaptureDraft; readAtMs: number; audio: CaptureAudioMatch };

/**
 * Ordinary edits are debounced; the facts that change MEANING are not.
 *
 * Long enough that a sentence of typing is one write rather than forty, short
 * enough that the exposure window — the text typed since the last commit — is
 * a fraction of a second of work. It is not a substitute for the immediate
 * writes: a recording starting, a Save dispatching and a draft beginning all
 * bypass it entirely.
 */
const DRAFT_WRITE_DEBOUNCE_MS = 400;

/**
 * The mic states in which UNRESOLVED AUDIO EXISTS on the device.
 *
 * The marker this drives contains no audio and never will (D-019 J-1). It
 * answers exactly one question after a process death: was there a recording
 * that never became text? `recorder-failed`, `no-words` and `idle` are all
 * FALSE — in those, either nothing was recorded or Brandon has already been
 * told what happened, so claiming a loss on relaunch would be a second,
 * inaccurate alarm.
 *
 * **S-04-02 NARROWED WHAT THIS MEANS, and the narrowing is the whole point.**
 * The persisted `recordingInProgress` is now this AND `pendingAudioId === null`
 * — that is, "there was audio and it is NOT durably kept". Once a safe stop
 * commits its Blob, the marker goes durably false in the same write that sets
 * the pointer, because the honest message on relaunch is no longer "it could
 * not be kept" but "here it is". A stale marker can therefore never hide a
 * valid finalized recording: the pointer is checked first.
 */
function hasUnresolvedRecording(mic: MicState): boolean {
  return (
    mic.kind === 'recording' ||
    mic.kind === 'stopping' ||
    mic.kind === 'stopped' ||
    mic.kind === 'transcribing' ||
    mic.kind === 'stt-failed'
  );
}

/**
 * Why a recording stopped safely: Brandon's tap, the D-008 hard cap, or a
 * D-018 interruption — Crewlink leaving the screen (backgrounding and screen
 * lock are one signal), or the microphone itself dying. Carried in the
 * `stopped` state because a recording that ended without Brandon's tap must
 * SAY why (PT §20), and the four reasons need four different sentences.
 */
type StopReason = 'user' | 'cap' | 'background' | 'microphone' | 'recovered';

/**
 * THE RECORDING STATE MODEL — S-03-03, extended by S-03-04. Explicit and
 * closed: every state the microphone can be in has a name, and impossible
 * combinations (two live recordings, two STT submissions for one recording, a
 * transcript before STT returns, a warning without a live recording) are
 * unrepresentable rather than merely avoided.
 *
 * `starting`     getUserMedia is in flight — the permission prompt may be up.
 *                Deliberately NOT labelled "recording": the microphone is not
 *                live yet, and claiming it is would be a small lie (PT §20).
 * `recording`    the microphone is live. `warned` is whether the D-008 soft
 *                warning has fired — carried IN the recording state, so it
 *                cannot fire twice for one recording and cannot survive into
 *                the next one, which starts with a fresh `warned: false`.
 * `stopping`     stop is finalizing — Brandon's tap and the hard cap both
 *                arrive here through ONE path. BOUNDED — the port's stop()
 *                times out rather than hanging (I-027 / D-017).
 * `stopped`      the recording completed SAFELY and waits, in memory only,
 *                for the explicit choice: Transcribe or Discard. `reason`
 *                says whether the D-008 cap did the stopping, because Brandon
 *                must be told why his recording ended without his tap.
 * `transcribing` the audio left for Crewlink's STT boundary. Cancellable,
 *                because a dead network can hold a fetch open a long time and
 *                the screen must never be stuck (I-026's product half).
 * `stt-failed`   transcription failed. THE AUDIO IS KEPT, in memory only, so
 *                Retry can resend the same recording without making Brandon
 *                say it again — it is still "required for the current
 *                operation" until a transcript exists or Brandon moves on.
 * `recorder-failed` the recording itself failed (start refused, stop timed
 *                out). There is no audio worth keeping (D-017: no salvage).
 * `no-words`     the round trip WORKED and returned empty text. A real result,
 *                not an error, and it must not render as one (PT §20).
 *
 * Failure states offer exactly the PT §7 fallbacks: record again, retry
 *                transcription (only where the audio is valid), type the note.
 */
type MicState =
  | { kind: 'idle' }
  | { kind: 'starting' }
  | { kind: 'recording'; startedAtMs: number; warned: boolean }
  | { kind: 'stopping' }
  /**
   * `durable` and `expired` are S-04-02 additions, and both exist to keep the
   * screen honest rather than to change what it does. `durable: false` means
   * the finalized recording is in memory ONLY — the store refused it — so the
   * screen must say it will not survive closing Crewlink instead of implying
   * the usual guarantee. `expired: true` means D-010's window was reached
   * while it sat here; the audio is untouched and the choices narrow to Retry
   * or Discard.
   */
  | { kind: 'stopped'; audio: Blob; reason: StopReason; durable: boolean; expired: boolean }
  | { kind: 'transcribing' }
  | { kind: 'stt-failed'; message: string; audio: Blob; durable: boolean }
  | { kind: 'recorder-failed'; message: string }
  | { kind: 'no-words' };

/**
 * The states in which the microphone pipeline owns the screen.
 *
 * `stopped` is deliberately one of them: a safely completed recording is an
 * unresolved decision, and letting Save (or more typing) proceed over it
 * would silently abandon audio Brandon just made. Discard is one tap away and
 * unlocks everything — nothing is thrown away except by him.
 */
function isMicBusy(mic: MicState): boolean {
  return (
    mic.kind === 'starting' ||
    mic.kind === 'recording' ||
    mic.kind === 'stopping' ||
    mic.kind === 'stopped' ||
    mic.kind === 'transcribing'
  );
}

/** `1:07` from elapsed milliseconds. The design's timer, in the light layout. */
function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / 1000);
  return `${String(Math.floor(totalSeconds / 60))}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

export function CaptureCreateScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { status: projectStatus, project } = useProject(projectId);

  const [note, setNote] = useState('');
  const [captureStatus, setCaptureStatus] = useState<CaptureStatus>('open');
  const [save, setSave] = useState<SaveState>({ kind: 'editing' });

  /**
   * VIEW-FIRST TRANSCRIPT REVIEW (PT §7). `edit` renders the textarea; `view`
   * renders the same `note` value as plain text with a deliberate Edit
   * control. The screen opens in `edit` (typed entry is the accepted S-03-02
   * surface) and switches to `view` when a transcript arrives — words first,
   * keyboard closed, nothing focused. Both modes render ONE value; there is no
   * mode in which what Brandon sees and what Save sends can differ.
   */
  const [noteMode, setNoteMode] = useState<'edit' | 'view'>('edit');

  // Created once and stable for the component's lifetime, like the probe
  // before it: lazy state rather than a ref because `isSupported()` is read
  // during render, and a ref cannot be (react-hooks/refs).
  const [recorder] = useState(() => selectRecorder());
  // The same construction for the same reason. Held during ACTIVE RECORDING
  // ONLY (D-008) — see the effect keyed on the recording state below.
  const [keepAwake] = useState(() => selectKeepAwake());
  const [mic, setMic] = useState<MicState>({ kind: 'idle' });
  const [elapsedMs, setElapsedMs] = useState(0);

  /**
   * The CURRENT mic state, readable from timer callbacks. The hard-cap timer
   * fires outside React's render cycle, and a state value closed over at
   * schedule time can be a render old — this ref is what lets the shared stop
   * path check "is a recording ACTUALLY still live" at the moment it runs.
   */
  const micRef = useRef(mic);
  useEffect(() => {
    micRef.current = mic;
  });

  /**
   * THE STALE-RESPONSE GUARD. Every user action that starts or abandons a
   * microphone operation increments this; every async continuation captures
   * the value it started under and applies its result ONLY if the value is
   * unchanged. The bug class this kills: recording A's transcription limps in
   * late — after Brandon cancelled it and recorded B — and overwrites B's
   * transcript. With the token check, response A compares its captured token
   * against the current one, sees it lost ownership, and is discarded.
   *
   * A counter rather than an AbortController because nothing here needs the
   * request torn down mid-flight — only its RESULT refused. The smallest
   * mechanism that makes the guarantee.
   */
  const micOp = useRef(0);

  /**
   * Synchronous single-flight for mic taps, same pattern as `inFlight` below:
   * state updates are asynchronous, so two Start taps in one tick would both
   * read an idle state. A ref is written synchronously and closes the window —
   * the disabled button is the visible half, this is the half that holds.
   */
  const micTapBusy = useRef(false);

  /**
   * ONE STOP PER RECORDING — the S-03-04 race the hard cap creates. Brandon's
   * Stop tap and the cap timer can arrive in the same instant, and `micTapBusy`
   * alone has a hairline gap: it clears when the first stop settles, a breath
   * before React commits the state the second caller would be refused on. This
   * latch is set synchronously by whichever stop wins, cleared only when a NEW
   * recording genuinely starts, so the loser of the race is refused no matter
   * which side of that commit it lands on. A second `stop()` against a
   * torn-down recorder would throw "No recording is in progress" and paint a
   * recorder failure over a recording that actually completed fine.
   */
  const stopIssued = useRef(false);

  /**
   * ONE OWNER FOR THE HELD AUDIO. Transcribe and Discard are two buttons over
   * the same Blob, and two taps in one tick would both read the `stopped`
   * state — React has not committed either transition yet. Whichever action
   * runs first claims the audio here, synchronously; the other returns having
   * done nothing. Re-armed at each state that OFFERS a claim: a stop
   * completing (`stopped`), and a transcription failing (`stt-failed`, where
   * Retry is the claiming action).
   *
   * Deliberately NOT `micTapBusy`: that latch would have to be held across
   * the whole STT await to mean anything, and holding it that long makes
   * Cancel → record again dead until a network request nobody is waiting for
   * settles. This latch settles the ownership question synchronously and then
   * gets out of the way.
   */
  const audioClaimed = useRef(false);

  /**
   * THE IDEMPOTENCY KEY — generated ONCE for this draft and reused on every
   * retry of it.
   *
   * This is the whole mechanism (D-002, D-016, INV-T06). A fresh key per tap
   * would defeat it **silently**: two taps on a poor connection would become
   * two Captures, and nothing on screen would look wrong. Lazy `useState`
   * rather than `useRef` so it is created exactly once and is safe to read
   * during render.
   *
   * It is deliberately NOT regenerated after a failure — a failure is exactly
   * when the same key matters most, because the first attempt may in fact have
   * reached the server.
   *
   * It IS regenerated by "Capture next", and only there: that is a genuinely
   * new Capture, and reusing the completed draft's key would make the second
   * note silently return the first Capture instead of saving.
   */
  // Typed `string`, not the inferred `${string}-${string}-…` template literal
  // `crypto.randomUUID()` returns: a RESTORED key comes off disk as a plain
  // string, and the narrower inferred type would refuse it. The value's shape
  // is guaranteed by where it is generated, not by the state's type.
  const [clientCaptureUuid, setClientCaptureUuid] = useState<string>(() => crypto.randomUUID());

  /**
   * SINGLE-FLIGHT GUARD, in a ref rather than state.
   *
   * State updates are asynchronous, so two taps dispatched in the same tick
   * would both read `save.kind === 'editing'` and both call the writer. A ref
   * is written synchronously and closes that window. The disabled button is
   * the visible half; this is the half that actually holds.
   *
   * The database's owner-scoped idempotency is the backstop underneath both.
   */
  const inFlight = useRef(false);

  /* ======================================================================
   * THE DURABLE DRAFT — M4 / S-04-01
   * ====================================================================== */

  /**
   * `useOptionalSession`, not `useSession`, and the difference is the whole
   * user-scoping rule: with no resolved signed-in identity the owner is
   * `null`, and a `null` owner reads nothing and writes nothing. There is no
   * anonymous draft, no "current device" draft, and no path by which one
   * user's words reach another's screen.
   */
  const ownerUserId = sessionOwnerUserId(useOptionalSession());
  const location = useLocation();
  const recoverIntent =
    (location.state as { recoverDraft?: boolean } | null)?.recoverDraft === true;
  // Navigation identity, so answering the card for a draft in ANOTHER Project
  // re-runs the read against the Project it actually belongs to.
  const locationKey = location.key;

  /**
   * THE READ IS IDENTIFIED BY WHAT IT WAS A READ OF.
   *
   * The lookup state carries the key it answered, and the entry below is
   * `checking` whenever that key is not the current one. That is a PURE
   * derivation, and it is why nothing in this file resets the entry with a
   * synchronous `setState` inside an effect: changing user, Project or
   * navigation invalidates the previous answer by arithmetic rather than by a
   * cascading render.
   *
   * It also closes a real race. While an answer is stale, `draftEntry` is
   * `checking`, and persistence is off in `checking` — so a screen that has
   * navigated to another Project's Capture cannot write an empty draft over
   * the stored one in the window before the new read lands.
   */
  const readKey = `${ownerUserId ?? ''}|${projectId ?? ''}|${locationKey}`;
  const [lookup, setLookup] = useState<{ key: string; entry: DraftEntry } | null>(null);
  const draftEntry: DraftEntry = lookup?.key === readKey ? lookup.entry : { kind: 'checking' };

  /**
   * Set only by a restore, and only when the stored draft said a recording was
   * unresolved. It is the honest admission Jerry asked for (D-019 J-1) —
   * displayed until Brandon records again, because it is telling him something
   * was lost, not reporting a transient state.
   */
  /**
   * WHY A RECORDING IS NOT HERE — and the two answers are NOT the same
   * (M4 / S-04-03, closing I-038).
   *
   * `interrupted` — a recording was still RUNNING when Crewlink died. Nothing
   *                 was ever safely finished, so nothing could be kept. That
   *                 is D-019 (J-1)'s honest loss, and its wording is unchanged.
   * `unavailable` — the draft points at a finished recording that is not
   *                 there. Usually damage or a write that never landed — but
   *                 it is ALSO what a deliberate Discard looks like when the
   *                 pointer-clear silently failed, and telling a man his
   *                 recording "could not be kept" when he threw it away
   *                 himself is a small lie. The wording is deliberately
   *                 generic and true of both.
   */
  const [recordingLost, setRecordingLost] = useState<null | 'interrupted' | 'unavailable'>(null);
  /**
   * Whether the draft store can survive the app closing — resolved, not
   * assumed (I-036). `null` until the first read comes back, because before
   * that the storage layer has not learned whether its open failed.
   */
  const [draftStorageDurable, setDraftStorageDurable] = useState<boolean | null>(null);
  /**
   * THE DURABLE POINTER, mirrored in React state — M4 / S-04-02.
   *
   * Non-null exactly while a safely finalized recording is stored and
   * unresolved. Every durable write of the draft carries this value, and it is
   * the ONLY thing that decides whether audio is unresolved: not the Blob in
   * `mic`, not the note, not the marker, not age.
   */
  const [pendingAudioId, setPendingAudioId] = useState<string | null>(null);
  /**
   * The draft's own birth instant, so "how old" survives every later write.
   * `null` until the first persist stamps it — the clock is read in an effect
   * or a handler, never during render.
   */
  const draftCreatedAtMs = useRef<number | null>(null);

  const restoreDraft = useCallback((draft: CaptureDraft, audio: RecoveredAudio) => {
    // EXACTLY what was stored, transformed by nothing. The recovered text is
    // the Final Accepted Transcript candidate Brandon last saw; regenerating
    // it through STT or summarising it would replace his words with new ones.
    setNote(draft.note);
    setCaptureStatus(draft.status);
    // THE SAME KEY. A new one here would defeat idempotency exactly where it
    // matters most — a Save whose fate is unknown (D-002, D-016).
    setClientCaptureUuid(draft.clientCaptureUuid);
    setNoteMode('edit');
    draftCreatedAtMs.current = draft.createdAtMs;
    // A restored screen owns the microphone: anything stale from a previous
    // session on this component instance is orphaned.
    micOp.current += 1;

    /**
     * THE RECOVERED RECORDING — M4 / S-04-02.
     *
     * A valid match lands straight back in the SAME `stopped` state the
     * recording was in when Crewlink died: the audio waiting for an explicit
     * Transcribe or Discard. Nothing auto-transcribes, nothing auto-saves,
     * nothing creates a Capture — recovery restores a decision, not a result.
     *
     * A `lost` match is the honest half. The draft pointed at a recording that
     * is missing or damaged, so the text is restored and the loss is stated;
     * the dead pointer is cleared separately so the same impossible recording
     * cannot be offered forever.
     */
    if (audio.match === 'ready' || audio.match === 'expired') {
      audioClaimed.current = false;
      setPendingAudioId(draft.pendingAudioId);
      setRecordingLost(null);
      setMic({
        kind: 'stopped',
        audio: audio.record.blob,
        reason: 'recovered',
        durable: true,
        expired: audio.match === 'expired',
      });
    } else {
      // `lost` means a recording WAS made and cannot be given back; the
      // S-04-01 marker means the same thing for a recording interrupted before
      // it finished. Both deserve the honest sentence.
      setPendingAudioId(null);
      // A pointer that led nowhere is `unavailable`; an interrupted recording
      // is `interrupted`. When both are somehow true, the interrupted message
      // is the more specific and the more useful one.
      setRecordingLost(
        draft.recordingInProgress ? 'interrupted' : audio.match === 'lost' ? 'unavailable' : null,
      );
      setMic({ kind: 'idle' });
    }
    setSave(
      draft.lifecycle === 'pending-save' && draft.pendingPayload
        ? { kind: 'waiting-to-send', payload: draft.pendingPayload }
        : { kind: 'editing' },
    );
  }, []);

  /**
   * Resolve what the durable pointer and the stored recording MEAN together.
   *
   * One function so the read effect and the Continue button cannot drift into
   * two different opinions about whether a recording is offerable. It reads;
   * it never deletes.
   */
  const resolveRecoveredAudio = useCallback(
    async (owner: string, draft: CaptureDraft): Promise<RecoveredAudio> => {
      try {
        const record = await getCaptureAudioStore().read(owner);
        const match = matchCaptureAudio(draft, record, Date.now());
        if ((match === 'ready' || match === 'expired') && record) return { match, record };
        return { match: match === 'none' ? 'none' : 'lost' };
      } catch (error) {
        reportError(error, 'capture.audio.read');
        // Unreadable is not the same as absent. If the draft points at a
        // recording we cannot read, the honest answer is that it is lost —
        // never that it is ready.
        return { match: draft.pendingAudioId === null ? 'none' : 'lost' };
      }
    },
    [],
  );

  /**
   * A pointer to a recording that cannot be given back, retired.
   *
   * Called ONLY after the honest-loss state is on screen. Without it the same
   * impossible recording would be re-detected, re-reported and re-mourned on
   * every single launch — an app that keeps telling you about the same lost
   * work is not being honest, it is nagging.
   *
   * The typed text is carried through untouched; only the pointer changes. It
   * writes through the same recoverability rule the persist path uses, so a
   * draft left with nothing in it clears its slot rather than becoming a husk.
   */
  const clearDeadAudioPointer = useCallback(async (owner: string, draft: CaptureDraft) => {
    try {
      const cleaned: CaptureDraft = {
        ...draft,
        recordingInProgress: false,
        pendingAudioId: null,
        updatedAtMs: Date.now(),
      };
      const store = getCaptureDraftStore();
      if (isRecoverableCaptureDraft(cleaned)) await store.write(cleaned);
      else await store.clear(owner);
      // Whatever unusable remnant the pointer named goes with it.
      await getCaptureAudioStore().clear(owner);
    } catch (error) {
      reportError(error, 'capture.audio.deadPointerCleanup');
    }
  }, []);

  /**
   * THE ONE READ of the durable slot, and — as of S-04-02 — of the temporary
   * audio beside it.
   *
   * **Reading still never resolves anything.** The two writes this path can
   * make are both cleanups of things that are already unusable, never of a
   * recording somebody might still want:
   *
   *   * an ORPHAN Blob is deleted — but only when a POSITIVELY READ draft
   *     proves the mismatch (R1 F-2). A `null` draft read is never deletion
   *     authority: the store fails by resolving null, so absence and read
   *     failure are indistinguishable, and the ambiguous answer must delete
   *     nothing. A proven orphan is unreachable by definition — the offer rule
   *     can never surface it — so removing it costs nobody anything.
   *   * a DEAD POINTER (points at a recording that is missing or damaged) is
   *     cleared, but only AFTER the honest-loss state has been put in front of
   *     Brandon, so he is told once and not told forever.
   *
   * Neither touches the typed text.
   */
  useEffect(() => {
    let cancelled = false;

    const read = async (): Promise<{
      draft: CaptureDraft | null;
      audio: RecoveredAudio;
    }> => {
      // A `null` owner resolves without touching either store: no identity, no
      // read, nothing to restore, and nothing will be persisted either.
      if (ownerUserId === null) return { draft: null, audio: { match: 'none' } };

      const draft = await getCaptureDraftStore().read(ownerUserId);
      const record = await getCaptureAudioStore().read(ownerUserId);

      // Orphan sweep, on the narrow definition only (see isOrphanCaptureAudio):
      // a null draft read authorizes nothing, because it could equally be a
      // failed read.
      if (record && isOrphanCaptureAudio(record, draft)) {
        try {
          await getCaptureAudioStore().clear(ownerUserId);
        } catch (error) {
          reportError(error, 'capture.audio.orphanCleanup');
        }
        return { draft, audio: { match: 'none' } };
      }

      if (!draft) return { draft: null, audio: { match: 'none' } };
      return { draft, audio: await resolveRecoveredAudio(ownerUserId, draft) };
    };

    void read()
      .then(({ draft, audio }) => {
        if (cancelled) return;
        // THE ANSWER IS ONLY REAL NOW (I-036). The storage adapter cannot know
        // its open failed until that open settles, and this read is the first
        // thing that waits for it. Asked any earlier, `durable` is optimistic
        // by construction.
        setDraftStorageDurable(ownerUserId === null ? null : getCaptureDraftStore().durable);
        if (!draft || !isRecoverableCaptureDraft(draft)) {
          setLookup({ key: readKey, entry: { kind: 'ready' } });
          return;
        }
        if (recoverIntent && draft.projectId === projectId) {
          restoreDraft(draft, audio);
          setLookup({ key: readKey, entry: { kind: 'ready' } });
          // The dead pointer is cleared only now — the loss is on screen, so
          // clearing it cannot hide anything from him. Done after the restore
          // so the draft written carries the text he can see.
          if (audio.match === 'lost' && ownerUserId !== null) {
            void clearDeadAudioPointer(ownerUserId, draft);
          }
          return;
        }
        // Found, but not asked for. Offer it — never adopt it (D-019 J-3).
        setLookup({
          key: readKey,
          entry: { kind: 'offer', draft, readAtMs: Date.now(), audio: audio.match },
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // An unreadable store costs recovery, never correctness. The draft and
        // the recording, if there are any, stay on disk untouched — and the
        // durability question is answered here too, because a read that threw
        // is exactly when Brandon most needs to be told.
        reportError(error, 'capture.draft.read');
        setDraftStorageDurable(ownerUserId === null ? null : getCaptureDraftStore().durable);
        setLookup({ key: readKey, entry: { kind: 'ready' } });
      });

    return () => {
      cancelled = true;
    };
  }, [
    ownerUserId,
    projectId,
    recoverIntent,
    readKey,
    restoreDraft,
    clearDeadAudioPointer,
    resolveRecoveredAudio,
  ]);

  const projectName = project?.name ?? null;
  // "There was audio and it is NOT durably kept." A stored pointer means the
  // recording IS kept, so the honest-loss marker must be false.
  const unresolvedRecording = hasUnresolvedRecording(mic) && pendingAudioId === null;
  const draftLifecycle =
    save.kind === 'saving' || save.kind === 'failed-ambiguous' || save.kind === 'waiting-to-send'
      ? 'pending-save'
      : 'editing';
  // The frozen payload's fields, held as PRIMITIVES so the effect below can
  // depend on their values rather than on an object rebuilt every render.
  const pendingSave =
    save.kind === 'saving' || save.kind === 'failed-ambiguous' || save.kind === 'waiting-to-send'
      ? save.payload
      : null;
  const pendingProjectId = pendingSave?.projectId ?? null;
  const pendingUuid = pendingSave?.clientCaptureUuid ?? null;
  const pendingTranscript = pendingSave ? pendingSave.transcript : null;
  const pendingTimezone = pendingSave?.timezone ?? null;
  const pendingStatus = pendingSave?.status ?? null;

  /**
   * Whether this screen may write the durable slot at all.
   *
   * `false` in four cases, each of them a rule rather than an optimisation: no
   * authenticated owner, the slot not yet read or currently being offered back,
   * no Project to name, and a Capture that is already SAVED — after server
   * truth exists, a local draft is not recovery material, it is a duplicate
   * waiting to be offered.
   */
  const persistDraft =
    ownerUserId !== null &&
    draftEntry.kind === 'ready' &&
    projectId !== undefined &&
    projectName !== null &&
    save.kind !== 'saved';

  /**
   * The current draft, readable from the visibility listener and from the
   * pre-dispatch write. Assigned INSIDE the persist effect — the clock and the
   * created-at ref are read there, where reading them is legal and where the
   * value is one React has actually committed.
   */
  const latestDraft = useRef<CaptureDraft | null>(null);

  /**
   * The one write path. A draft that is no longer worth recovering CLEARS the
   * slot rather than leaving an empty husk behind, so the recovery card can
   * never offer Brandon a Capture with nothing in it.
   *
   * Never throws — a storage failure must not be able to break typing,
   * recording or saving.
   *
   * **THE BOOLEAN IS NOT A DURABILITY PROOF, and must never be read as one**
   * (M4 / S-04-03, closing I-037; the S-04-02 R1 review rejected exactly that
   * reading). It means only that nothing THREW. The storage layer fails by
   * RESOLVING — a degraded adapter, an aborted transaction and a closed
   * connection all return normally and simply change nothing — so a `true`
   * here is consistent with the disk being untouched.
   *
   * Every D-010-critical caller therefore proves durability by READING THE
   * RECORD BACK and comparing it to the exact expected state: the safe-stop
   * pointer commit before a Blob is written, and the transcript commit before
   * any audio is deleted. This boolean is a cheap early exit for those paths,
   * never their evidence.
   */
  const writeDraft = useCallback(async (draft: CaptureDraft): Promise<boolean> => {
    try {
      const store = getCaptureDraftStore();
      if (isRecoverableCaptureDraft(draft)) await store.write(draft);
      else await store.clear(draft.ownerUserId);
      return true;
    } catch (error) {
      reportError(error, 'capture.draft.write');
      return false;
    }
  }, []);

  /**
   * DEBOUNCED FOR TYPING, IMMEDIATE FOR MEANING.
   *
   * The urgent key is the set of facts whose loss would produce a WRONG
   * outcome rather than a stale one: whether a recording is unresolved (the
   * honest "it could not be kept" sentence depends on it), whether a Save is
   * pending (the reconciliation depends on it), and which draft this is (a new
   * Capture must not inherit the previous one's slot). Any change to those is
   * written at once; ordinary keystrokes wait out the debounce.
   */
  const urgentDraftKey = useRef<string | null>(null);
  useEffect(() => {
    if (!persistDraft || ownerUserId === null || projectId === undefined || projectName === null) {
      latestDraft.current = null;
      return undefined;
    }

    draftCreatedAtMs.current ??= Date.now();
    const draft: CaptureDraft = {
      schemaVersion: CAPTURE_DRAFT_SCHEMA_VERSION,
      ownerUserId,
      projectId,
      projectName,
      clientCaptureUuid,
      note,
      status: captureStatus,
      lifecycle: draftLifecycle,
      recordingInProgress: unresolvedRecording,
      pendingAudioId,
      createdAtMs: draftCreatedAtMs.current,
      updatedAtMs: Date.now(),
      pendingPayload:
        pendingProjectId !== null &&
        pendingUuid !== null &&
        pendingTimezone !== null &&
        pendingStatus !== null
          ? {
              projectId: pendingProjectId,
              clientCaptureUuid: pendingUuid,
              transcript: pendingTranscript,
              timezone: pendingTimezone,
              status: pendingStatus,
            }
          : null,
    };
    latestDraft.current = draft;

    const key = `${String(draft.recordingInProgress)}|${draft.lifecycle}|${draft.clientCaptureUuid}|${draft.pendingAudioId ?? ''}`;
    const urgent = urgentDraftKey.current !== key;
    urgentDraftKey.current = key;

    if (urgent) {
      void writeDraft(draft);
      return undefined;
    }
    const timer = setTimeout(() => {
      void writeDraft(draft);
    }, DRAFT_WRITE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [
    persistDraft,
    ownerUserId,
    projectId,
    projectName,
    clientCaptureUuid,
    note,
    captureStatus,
    draftLifecycle,
    unresolvedRecording,
    pendingAudioId,
    pendingProjectId,
    pendingUuid,
    pendingTranscript,
    pendingTimezone,
    pendingStatus,
    writeDraft,
  ]);

  /**
   * The last moment the app is reliably told anything: `hidden`.
   *
   * A BELT, NOT THE BRACES. The debounce above is what actually makes the
   * draft durable, because Android is under no obligation to deliver this
   * event before killing the process. This flush simply removes the last
   * fraction of a second of typing from the exposure window when the event
   * does arrive — it is an improvement on the guarantee, never the guarantee.
   */
  useEffect(() => {
    const onVisibilityChange = () => {
      const draft = latestDraft.current;
      if (document.visibilityState === 'hidden' && draft) void writeDraft(draft);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [writeDraft]);

  /**
   * The temporary recording, removed. Used where the WHOLE unfinished Capture
   * ends — an explicit draft Discard, and a server-confirmed Save — so that no
   * finished Capture ever leaves a reachable recording behind it.
   */
  const clearRecoveryAudio = useCallback(async () => {
    if (ownerUserId === null) return;
    try {
      await getCaptureAudioStore().clear(ownerUserId);
    } catch (error) {
      reportError(error, 'capture.audio.clear');
    }
  }, [ownerUserId]);

  /** Terminal outcomes only — server truth, and Brandon's explicit discard. */
  const clearDraft = useCallback(async () => {
    if (ownerUserId === null) return;
    try {
      await getCaptureDraftStore().clear(ownerUserId);
    } catch (error) {
      reportError(error, 'capture.draft.clear');
    }
  }, [ownerUserId]);

  const blank = isBlankTranscript(note);
  const closedProject = project?.status === 'closed';
  const micBusy = isMicBusy(mic);

  // I-021's guarantee, moved from the probe to the product screen it was
  // always for: leaving mid-recording — Back, hardware back, the OS
  // destroying the view — must not strand a live microphone. `cancel()` is
  // safe and silent when nothing is recording; the recorder instance is
  // stable, so this runs exactly once, on unmount.
  useEffect(
    () => () => {
      recorder.cancel();
    },
    [recorder],
  );

  // The recording timer. Honest wall-clock measured by this app, like the
  // probe's "elapsed" — never a duration decoded from the audio.
  useEffect(() => {
    if (mic.kind !== 'recording') return undefined;
    const startedAtMs = mic.startedAtMs;
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - startedAtMs);
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, [mic]);

  /**
   * KEEP-AWAKE DURING ACTIVE RECORDING ONLY (D-008, S-03-04).
   *
   * Keyed on the one boolean that matters, so the lock is requested when a
   * recording GENUINELY starts (never during `starting`, where the permission
   * prompt may still be up) and the cleanup releases it on EVERY way out of
   * the recording state — Brandon's Stop, the hard cap, a stop failure,
   * Cancel, and unmount, which runs effect cleanups on its way down. No exit
   * path needs to remember to release, because none of them CAN forget.
   *
   * Fire-and-forget on both sides, with the rejection swallowed defensively:
   * the port promises never to reject, but keep-awake is a convenience, and a
   * convenience must never be able to break the recorder (PT §20 — nothing
   * here claims the lock was held; the port's answer is simply not consulted).
   */
  const recordingActive = mic.kind === 'recording';
  useEffect(() => {
    if (!recordingActive) return undefined;
    keepAwake.acquire().catch(() => undefined);
    return () => {
      keepAwake.release().catch(() => undefined);
    };
  }, [recordingActive, keepAwake]);

  /**
   * One transcription attempt, bound to the operation token it started under.
   *
   * Exactly one call to `transcribeRecording` per invocation, and the result
   * lands only while `op` still owns the screen — a stale response, however
   * late, changes nothing (see `micOp`).
   */
  const transcribe = useCallback(
    async (audio: Blob, op: number, durable: boolean) => {
      setMic({ kind: 'transcribing' });
      const outcome = await transcribeRecording(audio);
      if (micOp.current !== op) return; // Stale — a newer operation owns the screen.

      if (outcome.error !== null) {
        // The audio stays — in memory for this attempt, and ON DISK if the safe
        // stop stored it (S-04-02), so Retry does not make Brandon say it
        // again and a process death here does not lose it. **Nothing is
        // deleted because transcription failed**: offline, a timeout, a
        // provider error and a malformed response are all reasons to keep the
        // recording, not to throw it away (D-010).
        audioClaimed.current = false;
        setMic({ kind: 'stt-failed', message: outcome.error, audio, durable });
        return;
      }

      const transcript = outcome.transcript ?? '';
      const nextNote = isBlankTranscript(transcript)
        ? null
        : deliverTranscript(latestDraft.current?.note ?? note, transcript);

      /**
       * D-010'S CORE INVARIANT — TRANSCRIPT FIRST, DELETE SECOND, NEVER BOTH AT
       * ONCE AND NEVER THE OTHER WAY ROUND.
       *
       * The sequence D-010 exists to make impossible:
       *
       *     STT succeeds → audio deleted → app dies → transcript never
       *     persisted → BOTH copies of Brandon's words are gone.
       *
       * So the transcript and `pendingAudioId: null` go into ONE durable draft
       * write — and then, because the storage layer FAILS BY RESOLVING rather
       * than by throwing (a degraded adapter, an aborted transaction and a
       * silent no-op all look identical to the caller), **the write is not
       * believed. It is read back.** Only a persisted draft that provably
       * carries this exact transcript and a null pointer authorizes deleting
       * the audio (R1 S-04-02 finding F-1). Two writes instead of one would
       * open the same hole from the other side: a kill between them would
       * leave a Capture that still points at audio it has already turned into
       * text.
       *
       * If the delete then fails, nothing is broken. The pointer is already
       * null, so the leftover Blob is INERT — never offered, never transcribed
       * again, never shown — and startup orphan cleanup removes it later.
       *
       * If the DRAFT write fails — loudly or silently — the audio is
       * deliberately left alone and stays recoverable. The transcript is still
       * delivered to the screen, so nothing Brandon can see is lost; what he
       * has not got is durability, and the recording that could rebuild it is
       * exactly what is kept.
       */
      let resolvedDurably: boolean;
      const base = latestDraft.current;
      if (base && ownerUserId !== null && base.pendingAudioId !== null) {
        const expectedNote = nextNote ?? base.note;
        try {
          await writeDraft({
            ...base,
            note: expectedNote,
            recordingInProgress: false,
            pendingAudioId: null,
            updatedAtMs: Date.now(),
          });
          // THE READBACK IS THE PROOF. `writeDraft` returning true only means
          // nothing threw — the port resolves its failures. What decides the
          // audio's fate is the persisted record itself: same Capture, the
          // pointer genuinely null, and the note byte-for-byte the transcript
          // that was just delivered. Anything less keeps the recording.
          const persisted = await getCaptureDraftStore().read(ownerUserId);
          resolvedDurably =
            persisted !== null &&
            persisted.clientCaptureUuid === base.clientCaptureUuid &&
            persisted.pendingAudioId === null &&
            persisted.note === expectedNote;
        } catch (error) {
          reportError(error, 'capture.draft.transcriptCommit');
          resolvedDurably = false;
        }

        if (resolvedDurably) {
          try {
            await getCaptureAudioStore().clear(ownerUserId);
          } catch (error) {
            // Inert leftover, not a failure Brandon needs to know about.
            reportError(error, 'capture.audio.deleteAfterTranscript');
          }
        } else {
          reportError(
            new Error('transcript durability could not be verified; audio retained'),
            'capture.draft.transcriptVerify',
          );
        }
      } else {
        // Nothing durable pointed at this audio (a memory-only recording, or a
        // retry of one). There is no pointer to clear and nothing to delete.
        resolvedDurably = base?.pendingAudioId === null;
      }

      if (micOp.current !== op) return;
      if (resolvedDurably) setPendingAudioId(null);

      if (nextNote === null) {
        // The round trip WORKED and heard nothing. A real result (PT §20) —
        // surfaced as its own state, never invented into words.
        setMic({ kind: 'no-words' });
        return;
      }

      // THE DELIVERY. The transcript becomes (or joins) the note — the one
      // value Save sends — and review is VIEW-FIRST: text on screen, keyboard
      // closed, nothing focused. Editing is a deliberate tap on Edit.
      setNote(nextNote);
      setNoteMode('view');
      setMic({ kind: 'idle' });
    },
    [note, ownerUserId, writeDraft],
  );

  /**
   * THE ONE SAFE STOP PATH — S-03-04, widened by S-03-05. Brandon's Stop tap,
   * the D-008 hard cap, and D-018's preservable interruptions (backgrounding /
   * screen lock, a microphone that died) all come through here, which is what
   * makes "only one terminal path wins" a structural fact instead of a hope:
   * `stopIssued` is taken synchronously by the first arrival and stays taken
   * until a NEW recording starts, and `micRef` refuses a caller whose
   * recording is no longer the live one.
   *
   * A safe stop DELIVERS THE AUDIO TO A DECISION, never to STT: the completed
   * recording waits in the `stopped` state for an explicit Transcribe or
   * Discard. Nothing is submitted, claimed, or saved by the act of stopping.
   * A stop that cannot finalize follows I-027: no salvage, honest failure.
   */
  const stopRecording = useCallback(
    async (reason: StopReason) => {
      if (stopIssued.current || micTapBusy.current || micRef.current.kind !== 'recording') return;
      stopIssued.current = true;
      micTapBusy.current = true;
      const op = micOp.current; // The SAME operation continues into the decision.
      setMic({ kind: 'stopping' });

      let audio: Blob;
      try {
        // Bounded (I-027 / D-017): this settles within the port's stop timeout,
        // by result or by failure — never an infinite spinner. The bound holds
        // for the hard-cap and interruption stops exactly as for a tapped one,
        // and the port refuses an ERRORED recording outright (D-018), so a
        // maybe-corrupt blob can never reach this variable.
        const result = await recorder.stop();
        audio = result.blob;
      } catch {
        micTapBusy.current = false;
        if (micOp.current === op) {
          // The recorder failed to finalize, so what exists is of unknown
          // validity and was discarded by the port (D-017: no salvage). Honest
          // local failure, both fallbacks offered — the same rule when the cap
          // or an interruption did the stopping: no partial salvage, no
          // pretending.
          setMic({
            kind: 'recorder-failed',
            message:
              'The recording could not be finished, so it was discarded. Record again, or type the note below.',
          });
        }
        return;
      }
      micTapBusy.current = false;

      if (micOp.current !== op) return; // Orphaned mid-stop (unmount path).

      /**
       * THE DURABLE ORDER — M4 / S-04-02, and the order is the guarantee.
       *
       *   1. mint an audioId for THIS finalized recording
       *   2. commit the draft carrying `pendingAudioId` AND
       *      `recordingInProgress: false` — one write, one meaning
       *   3. commit the Blob
       *   4. only then show "your recording is kept"
       *
       * POINTER BEFORE BLOB, deliberately. The two orders fail differently and
       * only one of them fails honestly: a pointer with no Blob is DETECTABLE —
       * recovery validates the pair, finds nothing, and reports the loss — while
       * a Blob with no pointer is invisible, an orphan nothing will ever offer.
       * Writing the Blob first would mean a kill in between silently discarded a
       * recording that was already safely on disk.
       *
       * And the UI waits for step 3. Rendering "your recording is kept" before
       * the Blob commits would be a promise the device had not yet made.
       */
      const audioId = crypto.randomUUID();
      let durable = false;
      const base = latestDraft.current;

      if (base && ownerUserId !== null) {
        try {
          // The pointer must genuinely land before the Blob follows it. A Blob
          // written behind a pointer that failed is an orphan by definition —
          // nothing could ever reach it.
          await writeDraft({
            ...base,
            recordingInProgress: false,
            pendingAudioId: audioId,
            updatedAtMs: Date.now(),
          });
          /**
           * THE READBACK IS THE PROOF — R1 S-04-02 finding F-1. The storage
           * layer FAILS BY RESOLVING: a degraded adapter, an aborted
           * transaction and a silent no-op all return normally, so "the write
           * did not throw" is not evidence of anything. The persisted draft is
           * read back and must show exactly the state the recovery path will
           * later depend on — this Capture, THIS pointer, and the honest-loss
           * marker off. A Blob is written only behind a pointer that provably
           * exists; anything less stays in memory and says so.
           */
          const persisted = await getCaptureDraftStore().read(ownerUserId);
          const pointerDurable =
            persisted !== null &&
            persisted.clientCaptureUuid === base.clientCaptureUuid &&
            persisted.pendingAudioId === audioId &&
            !persisted.recordingInProgress;
          if (!pointerDurable) throw new Error('capture draft pointer could not be verified');
          const store = getCaptureAudioStore();
          await store.write(
            buildCaptureAudioRecord({
              ownerUserId,
              audioId,
              clientCaptureUuid: base.clientCaptureUuid,
              blob: audio,
              nowMs: Date.now(),
            }),
          );
          // TRUST NOTHING, VERIFY. The port never throws — it degrades to a
          // no-op — so "the write returned" is not evidence the Blob is there.
          // Reading it back through the same validation recovery will use is
          // the only honest way to claim it was kept.
          const stored = await store.read(ownerUserId);
          durable =
            store.durable &&
            stored !== null &&
            stored.audioId === audioId &&
            stored.clientCaptureUuid === base.clientCaptureUuid;
        } catch (error) {
          reportError(error, 'capture.audio.persist');
          durable = false;
        }

        /**
         * THE POINTER IS RETRACTED WHEN THE BLOB DID NOT LAND.
         *
         * Pointer-first is the right order for a CRASH — a pointer with no
         * Blob is detectable, a Blob with no pointer is invisible. But when we
         * are still alive and the Blob demonstrably did not commit, leaving
         * that pointer on disk would make the next launch report a lost
         * recording that is, in fact, still sitting in memory in front of
         * Brandon right now.
         *
         * So the durable state is corrected to what is actually true: there IS
         * unresolved audio (`recordingInProgress: true`) and it is NOT kept
         * (`pendingAudioId: null`). The persist effect cannot do this for us —
         * neither of the values it watches changed — which is exactly why it
         * is written here explicitly.
         */
        if (!durable) {
          try {
            await writeDraft({
              ...base,
              recordingInProgress: true,
              pendingAudioId: null,
              updatedAtMs: Date.now(),
            });
          } catch (error) {
            reportError(error, 'capture.audio.retractPointer');
          }
        }
      }

      if (micOp.current !== op) return; // Orphaned while committing.
      // The recording is complete and SAFE. It is nobody's yet: the fresh
      // claim latch means exactly one of Transcribe / Discard will own it.
      audioClaimed.current = false;
      setPendingAudioId(durable ? audioId : null);
      setMic({ kind: 'stopped', audio, reason, durable, expired: false });
    },
    [recorder, ownerUserId, writeDraft],
  );

  /**
   * D-018's failure half, for a recorder that ERRORED mid-recording: its audio
   * is of unknown validity and is never salvaged (D-017). One terminal path —
   * the same latch the safe stop takes — so an error racing a Stop tap or the
   * cap produces exactly one outcome; the microphone is released; and the
   * screen lands on the honest recoverable failure with both PT §7 fallbacks.
   */
  const failRecordingInterrupted = useCallback(() => {
    if (stopIssued.current || micTapBusy.current || micRef.current.kind !== 'recording') return;
    stopIssued.current = true;
    micOp.current += 1; // Anything stale from this recording is orphaned.
    recorder.cancel();
    setMic({
      kind: 'recorder-failed',
      message:
        'Recording was interrupted and could not be completed, so it was discarded. Record again, or type the note below.',
    });
  }, [recorder]);

  const handleStartRecording = useCallback(async () => {
    if (micTapBusy.current || micBusy) return;
    micTapBusy.current = true;
    // The "your recording is not here" notice has been acted on — he is
    // recording again. Leaving it up would keep reporting a loss he has
    // already answered.
    setRecordingLost(null);
    const op = ++micOp.current; // A new operation; anything in flight is orphaned.
    setMic({ kind: 'starting' });
    try {
      await recorder.start({
        // D-018 / S-03-05: the port reports, THIS recording's policy decides.
        // The token pins the callback to the recording it was created for — a
        // late event from a discarded or superseded recording changes nothing
        // (the port suppresses those too; this is the screen's own half).
        onInterruption: (cause) => {
          if (micOp.current !== op) return;
          if (cause === 'recorder-error') {
            // Broken recorder → no trusted audio → no salvage (D-017/D-018).
            failRecordingInterrupted();
          } else {
            // Microphone died → the platform finalized what it had → attempt
            // the normal safe stop and preserve it for the explicit choice.
            void stopRecording('microphone');
          }
        },
      });
      if (micOp.current === op) {
        // A genuinely new recording re-arms the one-stop latch and starts
        // unwarned — the D-008 warning belongs to a recording, not the screen.
        stopIssued.current = false;
        setElapsedMs(0);
        setMic({ kind: 'recording', startedAtMs: Date.now(), warned: false });
      } else {
        // Ownership moved while getUserMedia was up (unmount path). Release.
        recorder.cancel();
      }
    } catch {
      // Permission refused, no microphone, or the recorder could not start.
      // Nothing was recorded, so there is nothing to salvage — and the typed
      // note is right below (PT §7). Keep-awake was never acquired: it is
      // requested only when the recording state is genuinely entered.
      if (micOp.current === op) {
        setMic({
          kind: 'recorder-failed',
          message:
            'Recording could not start. Check the microphone permission, or type the note below.',
        });
      }
    } finally {
      micTapBusy.current = false;
    }
  }, [micBusy, recorder, failRecordingInterrupted, stopRecording]);

  const handleStopRecording = useCallback(() => stopRecording('user'), [stopRecording]);

  /**
   * D-018 cases 1 and 2 — backgrounding and screen lock, which are ONE signal
   * at this layer: `visibilitychange → hidden` fires for the home button, an
   * app switch, the call UI taking over, and the screen locking alike.
   * Crewlink never keeps recording off-screen (a backgrounded app's microphone
   * is silenced on modern Android anyway, and D-008 promises no background
   * recording) — the recording safe-stops, everything said is preserved, and
   * the Transcribe / Discard choice is waiting when Brandon returns.
   *
   * Registered only while a recording is live and removed on every exit, so
   * listeners cannot multiply across recordings or remounts, and a hidden
   * event while idle, deciding, transcribing or reviewing does nothing.
   */
  useEffect(() => {
    if (!recordingActive) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void stopRecording('background');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [recordingActive, stopRecording]);

  /**
   * THE D-008 TIMERS — one soft warning, one hard cap, scheduled against the
   * recording's own start instant and torn down the moment the recording state
   * is left, so a cleared recording can never be warned about or capped.
   *
   * The warning FIRES ONCE per recording by construction: `warned` lives in
   * the recording state, the timer is not rescheduled once it is true, and the
   * functional update refuses to touch any state but the same live recording.
   * The cap calls the SAME stop path as the Stop button — `stopRecording`
   * carries every race guard, so a cap firing against a recording that is
   * already stopping is refused there, not here.
   */
  useEffect(() => {
    if (mic.kind !== 'recording') return undefined;
    const startedAtMs = mic.startedAtMs;
    const sinceStart = () => Date.now() - startedAtMs;
    const timers: ReturnType<typeof setTimeout>[] = [];

    if (!mic.warned) {
      timers.push(
        setTimeout(
          () => {
            setMic((current) =>
              current.kind === 'recording' && !current.warned
                ? { ...current, warned: true }
                : current,
            );
          },
          Math.max(0, RECORDING_WARNING_MS - sinceStart()),
        ),
      );
    }

    timers.push(
      setTimeout(
        () => {
          void stopRecording('cap');
        },
        Math.max(0, RECORDING_HARD_CAP_MS - sinceStart()),
      ),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [mic, stopRecording]);

  /**
   * The explicit choice — TRANSCRIBE. The one and only submission path for a
   * completed recording: exactly one STT request per choice, taken under a
   * fresh operation token so anything stale is orphaned. `audioClaimed` is
   * the synchronous half that makes a same-tick Discard (or second Transcribe
   * tap) do nothing — one action owns the audio, decided before React commits.
   * `micTapBusy` is checked but deliberately not held: the claim latch is the
   * single-flight, and a latch held across the STT await would deaden
   * Cancel → record again for as long as a dead network keeps the fetch open.
   */
  const handleTranscribe = useCallback(async () => {
    if (micTapBusy.current || audioClaimed.current || mic.kind !== 'stopped') return;
    audioClaimed.current = true;
    const op = ++micOp.current;
    await transcribe(mic.audio, op, mic.durable);
  }, [mic, transcribe]);

  /**
   * The explicit choice — DISCARD. Throws away THIS unsaved recording only:
   * nothing is sent to STT, no Capture is created, no saved Capture is
   * touched, and **the typed note is left exactly as it is**. The state
   * transition drops the in-memory reference, the token bump orphans anything
   * stale, and the screen returns to a usable idle — type the note, or record
   * again (PT §7).
   *
   * S-04-02 adds the durable half, in the safe order: **clear the pointer
   * first, delete the Blob second.** A kill in between leaves an orphan
   * nothing points at, which cleanup removes quietly.
   *
   * **THE DELETION IS NOT CONDITIONAL ON THAT POINTER CLEAR — M4 / S-04-03,
   * Jerry's decision on I-038.** Every other D-010 path verifies its write by
   * reading it back before doing anything irreversible, and refuses to proceed
   * if it cannot. This one is deliberately the opposite, because the thing
   * being protected is the opposite: elsewhere the risk is destroying work
   * Brandon wanted, here the risk is KEEPING a recording he explicitly told
   * Crewlink to throw away. Refusing to delete because a bookkeeping write
   * could not be confirmed would honour the storage layer over the man holding
   * the phone.
   *
   * So the pointer clear is attempted, and the audio goes either way. If the
   * clear silently no-ops, a stale pointer survives — and the message it
   * eventually produces is the generic *"That recording is no longer
   * available"*, which is true of a deliberate Discard as much as of damage.
   * Dead-pointer cleanup then retires it so it cannot recur.
   */
  const discardRecording = useCallback(async () => {
    const base = latestDraft.current;
    if (ownerUserId === null) return;
    if (base && base.pendingAudioId !== null) {
      try {
        await writeDraft({
          ...base,
          recordingInProgress: false,
          pendingAudioId: null,
          updatedAtMs: Date.now(),
        });
      } catch (error) {
        // Best effort. It must not stop the deletion below.
        reportError(error, 'capture.audio.discard.pointer');
      }
    }
    try {
      // HE ASKED. In its own try so that nothing above can skip it.
      await getCaptureAudioStore().clear(ownerUserId);
    } catch (error) {
      reportError(error, 'capture.audio.discard');
    }
  }, [ownerUserId, writeDraft]);

  const handleDiscard = useCallback(() => {
    if (micTapBusy.current || audioClaimed.current || mic.kind !== 'stopped') return;
    audioClaimed.current = true;
    micOp.current += 1;
    setPendingAudioId(null);
    setMic({ kind: 'idle' });
    void discardRecording();
  }, [mic.kind, discardRecording]);

  const handleRetryTranscription = useCallback(async () => {
    // The claim latch is the single-flight here too: `stt-failed` re-armed it,
    // and two Retry taps in one tick must not become two STT requests.
    if (micTapBusy.current || audioClaimed.current || mic.kind !== 'stt-failed') return;
    audioClaimed.current = true;
    // A NEW operation token: if this retry races anything stale, the newest
    // attempt wins and the stale result is discarded.
    const op = ++micOp.current;
    await transcribe(mic.audio, op, mic.durable);
  }, [mic, transcribe]);

  const handleCancelRecording = useCallback(() => {
    if (mic.kind !== 'recording' && mic.kind !== 'transcribing') return;
    // Orphan whatever is in flight, release the microphone and the audio.
    // An accidental recording must not cost a pointless STT round trip, and a
    // hung network must not hold the screen hostage.
    micOp.current += 1;
    recorder.cancel();
    setMic({ kind: 'idle' });
  }, [mic.kind, recorder]);

  // Belt to the server's braces. A Project can be closed in another tab, or on
  // another device, while this screen is open — the writer's CL001 refusal is
  // the real boundary and this only spares Brandon a pointless round trip.
  useEffect(() => {
    if (projectStatus === 'ready' && closedProject && projectId) {
      void navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [projectStatus, closedProject, projectId, navigate]);

  /**
   * The one place a request leaves the device. Everything before this call is
   * pre-request by construction; everything after the `createCapture` line is
   * post-dispatch and therefore ambiguous on failure (F-1).
   */
  const dispatch = useCallback(
    async (
      client: NonNullable<ReturnType<typeof getSupabaseClient>>,
      payload: CreateCaptureInput,
    ) => {
      inFlight.current = true;
      setSave({ kind: 'saving', payload });

      /**
       * PERSIST BEFORE DISPATCH, AND AWAIT IT — the ordering S-04-01 exists
       * for.
       *
       * The window this closes is exactly one instruction wide in the source
       * and unbounded in the field: the request goes out, the server creates
       * the Capture, and the OS kills the app before the response lands. If
       * the frozen payload were written after — or written asynchronously
       * without waiting — the relaunched app would know nothing about a
       * Capture the server may already hold, and Brandon's only recovery
       * would be to type it again and risk a duplicate.
       *
       * `set()` on the store resolves at the IndexedDB transaction's commit,
       * so awaiting it means the payload is ON DISK, not queued, before the
       * first byte leaves. This is the one place in the file where a storage
       * write is allowed to delay a user action, and it is worth the
       * milliseconds.
       */
      const pending = latestDraft.current;
      if (pending) {
        await writeDraft({
          ...pending,
          lifecycle: 'pending-save',
          updatedAtMs: Date.now(),
          pendingPayload: {
            projectId: payload.projectId,
            clientCaptureUuid: payload.clientCaptureUuid,
            transcript: payload.transcript,
            timezone: payload.timezone,
            status: payload.status ?? 'open',
          },
        });
      }

      try {
        const capture = await createCapture(client, payload);
        // SERVER TRUTH ESTABLISHED — and only now is the local draft removed.
        // Clearing it any earlier would mean a failure between "cleared" and
        // "confirmed" lost the only recoverable copy; clearing it later would
        // leave a Capture that exists on the server also sitting on the phone
        // as an unfinished draft, and the next launch would offer to save it
        // again. The clear is awaited for the same reason the pending write is.
        await clearDraft();
        // A Capture that exists on the server must not leave a recording
        // behind it on the phone. In practice the transcript path already
        // cleared it — Save is disabled while a recording waits — but a saved
        // Capture is a terminal outcome, and terminal outcomes clean up.
        await clearRecoveryAudio();
        // "Saved" renders ONLY from a server-confirmed row (INV-T07). The
        // Capture number comes from `capture.capture_number` and is never
        // computed here — on a retry of a lost response, that row is the
        // server's ORIGINAL Capture, which is exactly what must be shown.
        setSave({ kind: 'saved', capture });

        // ORGANIZATION IS ASKED FOR *AFTER* THE CAPTURE IS SAFELY SAVED, and
        // its answer is never waited for (INV-T27, D-014, PT §10). The save has
        // already succeeded at this point and "Saved." is already on screen;
        // if organization fails, is refused admission, or never happens at all,
        // the Capture is completely valid and completely usable.
        //
        // **NO REASONING CALL HAPPENS INSIDE THE SAVE PATH.** This line runs
        // after `create_capture` has committed and returned a row — that is the
        // whole reason it sits here rather than anywhere above.
        //
        // A Capture with no transcript makes no model call: the endpoint
        // refuses it (there is nothing to organize) before any provider is
        // reached.
        requestCaptureOrganizationInBackground(capture.id);
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : String(caught);
        const code = (caught as { code?: string }).code;
        // THE DRAFT IS NOT CLEARED and nothing navigates away — but it does
        // LOCK. The first attempt may have reached the server, so the payload
        // frozen here is the only thing Retry is allowed to send: same key,
        // same text, same status, and "Saved. CAP-####" can then never claim
        // an edit the server never stored.
        setSave({
          kind: 'failed-ambiguous',
          message: describeCreateCaptureError(message, code),
          payload,
        });
      } finally {
        inFlight.current = false;
      }
    },
    [writeDraft, clearDraft, clearRecoveryAudio],
  );

  const handleSave = useCallback(async () => {
    if (inFlight.current || !projectId) return;
    // An unresolved recording or transcription can never race a Save: the
    // button below is disabled while the pipeline runs, and this is the
    // synchronous half of the same rule.
    if (isMicBusy(mic)) return;
    const client = getSupabaseClient();
    if (!client) {
      // PRE-REQUEST: nothing was dispatched, so nothing locks (F-1).
      setSave({ kind: 'failed', message: 'Crewlink is not connected to a server yet.' });
      return;
    }

    // An ambiguous failure retries the FROZEN payload, never current state.
    // The inputs are locked below, so the two cannot differ — the frozen copy
    // is what makes that a guarantee instead of a hope.
    //
    // A RECOVERED "Waiting to send" DRAFT TAKES THE SAME PATH, and that is the
    // point of routing them together: the payload came off disk with the same
    // `client_capture_uuid`, the same text, the same status and the same
    // timezone the first attempt used, so the server's owner-scoped
    // idempotency decides the outcome. First request landed → it returns the
    // ORIGINAL Capture. First request never landed → it creates one. Exactly
    // one Capture exists either way, and nothing on this device gets to guess
    // which happened.
    if (save.kind === 'failed-ambiguous' || save.kind === 'waiting-to-send') {
      await dispatch(client, save.payload);
      return;
    }

    if (blank) return;

    // The REAL device zone, read STRICTLY (F-2). `readCaptureTimeZone` never
    // substitutes: a runtime that reports nothing usable yields null, and the
    // save is refused HERE — pre-request, draft still editable — with the same
    // sentence the writer's own CL002 refusal carries. A Capture's timezone is
    // provenance, and an invented UTC would be indistinguishable from a real
    // one forever after.
    const timezone = readCaptureTimeZone();
    if (timezone === null) {
      setSave({
        kind: 'failed',
        message: describeCreateCaptureError('device reported no usable timezone', 'CL002'),
      });
      return;
    }

    await dispatch(client, {
      projectId,
      clientCaptureUuid,
      transcript: note,
      timezone,
      status: captureStatus,
    });
  }, [projectId, blank, clientCaptureUuid, note, captureStatus, save, mic, dispatch]);

  if (projectStatus === 'loading') {
    return (
      <main className="cl-screen capture-create">
        <p className="projects-loading" role="status">
          Loading this Project…
        </p>
      </main>
    );
  }

  if (projectStatus !== 'ready' || !project) {
    return (
      <main className="cl-screen capture-create">
        <p className="project-home-back">
          <Link className="cl-link" to="/">
            ‹ Projects
          </Link>
        </p>
        <h1 className="cl-screen-title">Project not available</h1>
        <p className="projects-empty-body">
          This Project does not exist, or it is not yours. Go back to Projects and pick one from the
          list.
        </p>
      </main>
    );
  }

  if (save.kind === 'saved') {
    return (
      <SavedConfirmation
        capture={save.capture}
        projectId={project.id}
        onCaptureNext={() => {
          // A NEW draft, so a NEW key. See the note on clientCaptureUuid.
          setClientCaptureUuid(crypto.randomUUID());
          setNote('');
          setCaptureStatus('open');
          setSave({ kind: 'editing' });
          // And a clean microphone: a fresh draft owns the screen, so any
          // stale transcription result is orphaned by the token bump.
          micOp.current += 1;
          setMic({ kind: 'idle' });
          setNoteMode('edit');
          // A NEW draft is also new for recovery purposes: its own age (stamped
          // by the next persist), and no inherited "your recording was lost"
          // notice from the finished one.
          draftCreatedAtMs.current = null;
          setRecordingLost(null);
        }}
      />
    );
  }

  const saving = save.kind === 'saving';
  // Locked means an attempt was DISPATCHED and its outcome is unknown here:
  // the ambiguous failure (F-1) and a "Waiting to send" draft recovered after
  // a process death are the same situation. The server may already hold this
  // draft, so what Retry sends must be what the server may have. The disabled
  // inputs are the visible half; the frozen payload is the half that
  // actually guarantees it.
  const locked = save.kind === 'failed-ambiguous' || save.kind === 'waiting-to-send';

  return (
    <main className="cl-screen capture-create">
      <p className="project-home-back">
        <Link className="cl-link" to={`/projects/${project.id}`}>
          ‹ {project.name}
        </Link>
      </p>

      {/* A stored draft that was NOT asked for. Offered, never adopted — and
          while it is offered nothing on this screen is persisted, so the
          unfinished Capture below cannot be overwritten by the new one above
          it. Continue for a draft in a DIFFERENT Project navigates to that
          Project's Capture route carrying the recover intent, because a
          Capture must never be restored under the wrong Project. */}
      {draftEntry.kind === 'offer' ? (
        <CaptureRecoveryCard
          draft={draftEntry.draft}
          nowMs={draftEntry.readAtMs}
          audio={draftEntry.audio}
          onContinue={() => {
            const found = draftEntry.draft;
            if (found.projectId === project.id) {
              // Restored in place — and the recording is re-resolved rather
              // than trusted from the card's snapshot, because the card may
              // have been on screen while the window expired.
              void (async () => {
                const audio =
                  ownerUserId === null
                    ? ({ match: 'none' } as RecoveredAudio)
                    : await resolveRecoveredAudio(ownerUserId, found);
                restoreDraft(found, audio);
                setLookup({ key: readKey, entry: { kind: 'ready' } });
                if (audio.match === 'lost' && ownerUserId !== null) {
                  await clearDeadAudioPointer(ownerUserId, found);
                }
              })();
              return;
            }
            void navigate(`/projects/${found.projectId}/capture`, {
              state: { recoverDraft: true },
            });
          }}
          onDiscard={() => {
            // Abandoning the whole unfinished Capture takes its temporary
            // recording with it — leaving the Blob would strand an orphan
            // holding audio for a Capture nobody is going to finish.
            void clearDraft();
            void clearRecoveryAudio();
            setLookup({ key: readKey, entry: { kind: 'ready' } });
          }}
        />
      ) : null}

      <header className="capture-create-header">
        <h1 className="cl-screen-title">New Capture</h1>
        {/* Which Project this belongs to is stated, not implied by navigation —
            a Capture filed under the wrong Project is a memory-product failure
            that is very hard to notice later. */}
        <p className="capture-create-project">
          in <strong>{project.name}</strong>{' '}
          <span className={`status-chip status-chip--${project.status}`}>
            {PROJECT_STATUS_LABEL[project.status]}
          </span>
        </p>
      </header>

      {/* TWO DIFFERENT TRUTHS, KEPT APART (D-019 J-1, and I-038's fix).

          `interrupted` is the original admission: a recording was RUNNING when
          Crewlink died, nothing was ever safely finished, and Jerry decided
          Crewlink does not guess at partial audio. That wording is unchanged.

          `unavailable` is the one S-04-03 corrected. It fires when the draft
          points at a finished recording that is not there — usually damage or
          a write that never landed, but ALSO what a deliberate Discard looks
          like when its pointer-clear silently failed. Telling a man his
          recording "could not be kept" when he threw it away himself is a
          small lie, so the sentence is deliberately generic and true either
          way. It states the fact and offers the way forward; it does not
          speculate about a cause it cannot know. */}
      {recordingLost !== null ? (
        <p className="capture-recording-lost" role="status">
          {recordingLost === 'interrupted'
            ? 'A recording was still unfinished when Crewlink closed, so the recording itself could not be kept. Record again, or type the note below.'
            : 'That recording is no longer available. Record again, or type the note below.'}
        </p>
      ) : null}

      {/* THE STORAGE THIS DEVICE COULD NOT GIVE US (I-036). Shown only once the
          answer is REAL — the store cannot know its open failed until that open
          settles — and only when it is false. It is about recovery after the
          app closes, nothing else: typing, recording, transcription and Save
          all work normally in this state, and nothing here says a word about
          Captures already saved to the server.

          It stands down while the audio warning is up: that sentence is more
          specific and more urgent, and stacking two storage warnings on one
          screen teaches Brandon to read neither. */}
      {draftStorageDurable === false && !(mic.kind === 'stopped' && !mic.durable) ? (
        <p className="capture-storage-warning" role="status">
          Crewlink can&rsquo;t keep unfinished work after the app closes right now. Save this
          Capture before closing Crewlink.
        </p>
      ) : null}

      <section className="capture-mic" aria-label="Voice capture">
        {!recorder.isSupported() ? (
          // No MediaRecorder / getUserMedia in this runtime. The honest state
          // — and typing is the permanent fallback, right below (PT §7).
          <p className="capture-create-hint">
            Voice recording is not available here. Type the note below.
          </p>
        ) : mic.kind === 'recording' ? (
          <>
            <p className="capture-mic-state" role="status">
              ● Recording
            </p>
            {/* The D-008 soft warning: the timer's visual state shifts and one
                short, non-blocking sentence appears — no modal, nothing to
                dismiss, no countdown, and recording simply continues. */}
            <p className={`capture-mic-timer${mic.warned ? ' capture-mic-timer--long' : ''}`}>
              {formatElapsed(elapsedMs)}
            </p>
            {mic.warned ? (
              <p className="capture-mic-warning" role="status">
                Long capture — you can save this and start another.
              </p>
            ) : null}
            <button
              type="button"
              className="capture-mic-button capture-mic-button--stop"
              onClick={() => void handleStopRecording()}
              aria-label="Stop recording"
            >
              <span className="capture-mic-stop-square" aria-hidden="true" />
            </button>
            <p className="capture-mic-hint">Tap to stop</p>
            <button type="button" className="capture-mic-cancel" onClick={handleCancelRecording}>
              Cancel recording
            </button>
          </>
        ) : mic.kind === 'starting' ? (
          <p className="capture-mic-state" role="status">
            Starting the microphone…
          </p>
        ) : mic.kind === 'stopping' ? (
          <p className="capture-mic-state" role="status">
            Finishing the recording…
          </p>
        ) : mic.kind === 'stopped' ? (
          <>
            {/* Why it stopped, honestly: a stop that was not Brandon's tap —
                the cap, leaving the screen, a dead microphone — must say so,
                or it reads as the app misbehaving (PT §20, D-018). */}
            <p className="capture-mic-state" role="status">
              {mic.reason === 'recovered'
                ? 'A recording is waiting from before Crewlink closed.'
                : mic.reason === 'cap'
                  ? `Recording stopped at the ${String(Math.round(RECORDING_HARD_CAP_MS / 60000))}-minute maximum.`
                  : mic.reason === 'background'
                    ? 'Recording stopped when Crewlink went to the background.'
                    : mic.reason === 'microphone'
                      ? 'Recording stopped because the microphone was disconnected.'
                      : 'Recording stopped.'}
            </p>
            {/* R1 finding F-2: Save is deliberately disabled while a recording
                waits here, so the reason is stated where the decision is made.
                A disabled control with no explanation reads as a broken one.

                S-04-02 splits this sentence three ways, and the split is the
                honesty: what Crewlink promises about this recording depends
                entirely on whether it actually reached the disk, and on
                whether D-010's window has passed. Saying "your recording is
                kept" over a memory-only Blob would be the exact lie this slice
                exists to prevent. */}
            {/* THE CLAUSE THAT MUST SURVIVE EVERY VARIANT: "Save is available
                once you choose." That is S-03-04 R1 finding F-2 — a disabled
                Save with no explanation reads as a broken app — and S-04-02
                changing what Crewlink can promise about the AUDIO is no reason
                to stop explaining the BUTTON. */}
            {mic.expired ? (
              <p className="capture-create-hint">
                This recording has been waiting longer than Crewlink keeps one, and it still has not
                been transcribed. Try transcribing it now, or discard it. Save is available once you
                choose.
              </p>
            ) : mic.durable ? (
              <p className="capture-create-hint">
                Your recording is kept, and it will still be here if Crewlink closes. Transcribe it
                into the note, or discard it. Save is available once you choose.
              </p>
            ) : (
              <p className="capture-create-hint">
                Your recording is here, but this device could not store it —{' '}
                <strong>it will be lost if Crewlink closes.</strong> Transcribe it now, or discard
                it. Save is available once you choose.
              </p>
            )}
            <button
              type="button"
              className="capture-mic-transcribe"
              onClick={() => void handleTranscribe()}
            >
              {mic.expired ? 'Retry transcription' : 'Transcribe'}
            </button>
            {/* "Discard recording", not "Discard": it must be unmistakable
                that this throws away the unsaved recording just made — never
                a saved Capture, never the typed note below. */}
            <button type="button" className="capture-mic-cancel" onClick={handleDiscard}>
              Discard recording
            </button>
          </>
        ) : mic.kind === 'transcribing' ? (
          <>
            <span className="capture-mic-spinner" aria-hidden="true" />
            <p className="capture-mic-state" role="status">
              Transcribing…
            </p>
            {/* Cancellable so a dead network can never strand the screen. The
                draft is untouched until a transcript actually arrives. */}
            <button type="button" className="capture-mic-cancel" onClick={handleCancelRecording}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {mic.kind === 'stt-failed' || mic.kind === 'recorder-failed' ? (
              <p className="cl-error" role="alert">
                {mic.message}
              </p>
            ) : mic.kind === 'no-words' ? (
              // A real result, not an error: the round trip worked and heard
              // nothing. Never rendered as a failure (PT §20).
              <p className="capture-create-hint" role="status">
                The recording came back with no words. Record again, or type the note below.
              </p>
            ) : null}
            <button
              type="button"
              className="capture-mic-button"
              onClick={() => void handleStartRecording()}
              aria-label={mic.kind === 'idle' ? 'Start recording' : 'Record again'}
              disabled={saving || locked}
            >
              <span className="capture-mic-glyph" aria-hidden="true" />
            </button>
            <p className="capture-mic-hint">
              {mic.kind === 'idle' ? 'Tap to record' : 'Record again'}
            </p>
            {mic.kind === 'stt-failed' ? (
              // The audio is still in memory, so the retry costs Brandon
              // nothing — he does not have to say it again.
              <button
                type="button"
                className="capture-mic-retry"
                onClick={() => void handleRetryTranscription()}
                disabled={saving || locked}
              >
                Retry transcription
              </button>
            ) : null}
          </>
        )}
      </section>

      <section className="capture-create-note">
        <div className="capture-note-labelrow">
          {noteMode === 'view' ? (
            // Not a <label>: in view mode there is no control to label.
            <span className="sheet-label">Note</span>
          ) : (
            <label className="sheet-label" htmlFor="capture-note">
              Note
            </label>
          )}
          {noteMode === 'view' ? (
            // The deliberate way into editing (PT §7). Never automatic, and
            // locked with everything else after an ambiguous save failure.
            <button
              type="button"
              className="capture-note-editbutton"
              onClick={() => {
                setNoteMode('edit');
              }}
              disabled={saving || locked}
            >
              Edit
            </button>
          ) : null}
        </div>
        {noteMode === 'view' ? (
          // VIEW-FIRST REVIEW: the transcript as text, keyboard closed,
          // nothing focused. This <p> renders the SAME `note` value the
          // textarea edits and Save sends — one truth, two presentations.
          <p className="capture-note-view">{note}</p>
        ) : (
          <textarea
            id="capture-note"
            className="capture-note-input"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            rows={8}
            placeholder="Type it, or tap the microphone and speak."
            disabled={saving || locked || micBusy}
          />
        )}
        <p className="capture-create-hint">
          This becomes the Capture&rsquo;s accepted text — spoken or typed, you approve it by
          saving.
        </p>
      </section>

      <section className="capture-create-status" aria-labelledby="capture-status-heading">
        <h2 id="capture-status-heading" className="sheet-label">
          Status
        </h2>
        <div className="capture-status-choices" role="group" aria-label="Capture status">
          {CAPTURE_STATUS_ORDER.map((option) => (
            <button
              key={option}
              type="button"
              className={`capture-status-choice capture-status-choice--${option}${
                option === captureStatus ? ' capture-status-choice--current' : ''
              }`}
              aria-pressed={option === captureStatus}
              disabled={saving || locked || micBusy}
              onClick={() => {
                setCaptureStatus(option);
              }}
            >
              {CAPTURE_STATUS_LABEL[option]}
            </button>
          ))}
        </div>
      </section>

      {save.kind === 'failed' || save.kind === 'failed-ambiguous' ? (
        <p className="cl-error" role="alert">
          {save.message}
        </p>
      ) : null}

      {/* THE LOCKED WORDING (D-002, PT §20). "Waiting to send" is what a
          dispatched-but-unconfirmed Capture is called — not "Saved", which
          would claim a server truth nobody on this device has, and not
          "Failed", which would claim the opposite. A restart is not a failure.
          It is deliberately a status, not an error: nothing has gone wrong. */}
      {save.kind === 'waiting-to-send' ? (
        <p className="capture-waiting" role="status">
          Waiting to send.
        </p>
      ) : null}

      {locked ? (
        // Says WHY the fields stopped responding, or the lock reads as a bug.
        <p className="capture-create-hint">
          {save.kind === 'waiting-to-send'
            ? 'Crewlink closed before the server confirmed this Capture, so the note and status are locked. Sending it again cannot create a second one — it is the same Capture.'
            : 'This save may have reached the server even though no confirmation came back, so the note and status are locked. Retry sends exactly the same Capture — it cannot create a second one.'}
        </p>
      ) : null}

      <button
        className="capture-save"
        type="button"
        onClick={() => void handleSave()}
        // An unresolved recording or transcription can never race a Save
        // (impossible-combination rule): disabled is the visible half, the
        // guard inside handleSave is the half that holds.
        //
        // `blank` does not disable a LOCKED save: what a locked save sends is
        // the frozen payload, not the visible note, and the server accepts a
        // Capture with no transcript. Refusing on blankness there would strand
        // a Capture the server may already hold with no way to reconcile it.
        disabled={saving || micBusy || (blank && !locked)}
      >
        {saving
          ? 'Saving…'
          : save.kind === 'waiting-to-send'
            ? 'Send now'
            : locked
              ? 'Retry Save'
              : 'Save Capture'}
      </button>

      {/* Honest about what happens next. The design export's line here mentions
          organizing in the background — that is M7, and nothing organizes
          anything yet, so claiming it would be a lie about the product. */}
      <p className="capture-create-hint capture-create-hint--footer">
        {blank
          ? 'Type a note to save this Capture.'
          : 'Saved only when the server confirms it. Location and caption come later.'}
      </p>
    </main>
  );
}

/**
 * Design export S10 — the saved confirmation.
 *
 * Every value shown here comes from the SERVER-RETURNED ROW. Nothing about the
 * Capture is computed locally, which is the point of showing it at all: it is
 * the proof that the save landed.
 */
function SavedConfirmation({
  capture,
  projectId,
  onCaptureNext,
}: {
  capture: CaptureRow;
  projectId: string;
  onCaptureNext: () => void;
}) {
  return (
    <main className="cl-screen capture-create">
      <section className="capture-saved" aria-live="polite">
        <p className="capture-saved-mark" aria-hidden="true">
          ✓
        </p>
        <h1 className="cl-screen-title">Saved.</h1>
        <p className="capture-saved-detail">
          <strong>{formatCaptureNumber(capture.capture_number)}</strong> created.
        </p>
        <p className="capture-saved-status">
          <span className={`status-chip status-chip--capture-${capture.status}`}>
            {CAPTURE_STATUS_LABEL[capture.status]}
          </span>
        </p>
      </section>

      <div className="capture-saved-actions">
        {/* A button, not a Link to the same route: navigating to the route this
            screen already occupies would not remount it, so the finished
            draft's text, status and idempotency key would all survive into the
            next Capture. */}
        <button className="capture-save" type="button" onClick={onCaptureNext}>
          Capture next
        </button>
        <Link className="cl-link" to={`/projects/${projectId}`}>
          Done
        </Link>
      </div>
    </main>
  );
}
